import { useQuery } from "@tanstack/react-query"
import { getAllWorkOrders, GetAllWorkOrdersParams } from "@/features/work-orders/api/work-orders.api"
import { WORK_ORDERS } from "@/shared/constants/backend-api"
import { useCurrentSearchParams } from "@/shared/hooks/use-current-search-params"
import {
    getWorkOrderQueryParamsSchema,
    GetWorkOrderQueryParamsType,
    WORK_ORDERS_LIST_DEFAULT_LIMIT,
} from "../schemas/query-params.schema"
import { WorkOrdersListResponseType } from "./types/work-orders-hook.type"
import { useRegionId } from "@/shared/hooks/use-region-id"
import { useMemo } from "react"
import { useSession } from "@/features/auth/context/session.context"
import { UserRole } from "@/shared/constants/user-roles"
import { useEffect } from "react"

/**
 * Hook to fetch work orders list with pagination support
 *
 * @returns {Object} {
 *  data: WorkOrdersPaginatedList,
 *  isLoading: boolean,
 *  queryParams: GetWorkOrderQueryParamsType
 * }
 */
export function useWorkOrdersList(): WorkOrdersListResponseType {
    const queryParams = useWorkOrdersListQueryParams()
    const { regionId, isLoading: regionLoading } = useRegionId()
    const { session } = useSession()

    const finalQueryParams = useMemo<GetAllWorkOrdersParams>(() => {
        const hideFilters = [
            UserRole.VOLUNTEER,
            UserRole.HOMEOWNER,
            UserRole.Supporter,
        ].includes(session?.role as UserRole)

        const params: GetAllWorkOrdersParams = {
            ...queryParams,
            regionId: regionId ?? undefined,
        }

        // For Volunteers, only show Active work orders
        if (session?.role === UserRole.VOLUNTEER) {
            params.status = "Active"
        }
        // Privacy: Volunteers, Homeowners, Supporters get no filters (search only)
        if (hideFilters) {
            delete params.homeownerName
            delete params.city
            delete params.street
            delete params.ownerId
            delete params.createdDate
            delete params.teamName
            delete params.delegateTo
            delete params.workOrderType
            delete params.dateFrom
            delete params.dateTo
            delete params.progressMin
            delete params.progressMax
        }

        return params
    }, [queryParams, regionId, session?.role])

    const query = useQuery({
        queryKey: [WORK_ORDERS.getAll, finalQueryParams],
        queryFn: () => getAllWorkOrders(finalQueryParams),
        enabled: regionId !== null && regionId !== undefined && !regionLoading,
    })

    const combinedIsLoading = regionLoading || query.isLoading

    return {
        ...query,
        isLoading: combinedIsLoading as typeof query.isLoading,
        queryParams,
    }
}

/**
 * Hook to get work orders list query params,
 *
 * @returns {GetWorkOrderQueryParamsType}
 */
export function useWorkOrdersListQueryParams(): GetWorkOrderQueryParamsType {
    const searchParams = useCurrentSearchParams<GetWorkOrderQueryParamsType>()

    const parsedSearchParams = getWorkOrderQueryParamsSchema.safeParse({
        ...searchParams,
        page: Number(searchParams.page || 1),
        limit: Number(
            searchParams.limit || WORK_ORDERS_LIST_DEFAULT_LIMIT
        ),
        sortBy: searchParams.sortBy || "createdAt",
        sortOrder: searchParams.sortOrder || "desc",
    })

    if (!parsedSearchParams.success) {
        throw new Error("Invalid search params")
    }
    return parsedSearchParams.data
}
